import React, { Component } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import ReactGA from 'react-ga';
import { Flex, Box } from '@rebass/grid';
import animate from 'app/helpers/animations/searchResults';

import WithTitle from 'app/layouts/WithTitle';
import UnlockForm from 'app/components/UnlockForm';
import Report from 'app/components/Report';
import { isMobile } from 'app/helpers/utils';
import { stickyTop } from 'app/redux/actions';
import BackgroundImages from 'app/ui_components/BackgroundImages';
import { P, Button } from 'app/ui_components/tags';
import CriteriaPreview from 'app/components/CriteriaPreview';
import ResetButton from 'app/components/ResetButton';
import ResultsActionsColumn from 'app/components/ResultsActionsColumn';

const RedHighlight = styled.span`
  color: #fff;
  background: #d35047;
  padding: 0 2px;
  white-space: nowrap;
`;

const ContentContainer = styled(Flex).attrs({})`
  background: #fcfcfc;
  transition: ease 0.5s;
  flex-grow: 1;
`;

class SearchResults extends React.Component {
  static propTypes = {
    usFoods: PropTypes.bool.isRequired,
    layout: PropTypes.object.isRequired,
    unlocked: PropTypes.bool.isRequired,
    failCount: PropTypes.number.isRequired,
    failed: PropTypes.bool.isRequired,
    name: PropTypes.string,
    domain: PropTypes.string,
    isBentobox: PropTypes.bool.isRequired,
    isStaff: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    usFoods: false,
    isStaff: false
  };

  constructor(props) {
    super(props);
    this.upperBound = document.getElementById('topbar').clientHeight;
    this.id = 'SearchResults';
    this.state = {
      shouldFixSidebar: false,
    };
  }

  componentDidMount() {
    animate(this.props.failCount);
    if (!isMobile) window.addEventListener('scroll', this.handleScroll);
  }

  componentWillUnmount() {
    if (!isMobile) window.removeEventListener('scroll', this.handleScroll);
  }

  handleScroll = e => {
    const { id, upperBound, state: { shouldFixSidebar } } = this;
    const top = document.getElementById(id).getBoundingClientRect().top;
    if (top < upperBound && !shouldFixSidebar) {
      this.setState({ shouldFixSidebar: true });
    } else if (top > upperBound && shouldFixSidebar) {
      this.setState({ shouldFixSidebar: false });
    }
  };

  getTitle(message = null) {
    const { usFoods, failed, isBentobox, sections, domain, unlocked } = this.props;
    if (failed) message = 'Uh oh. We couldn’t properly evaluate this website';
    else if (unlocked) {
      const textTitle = usFoods ? 'Thanks for being a loyal US Foods customer!' : `${domain} \n is Ready for Optimization`;

      message = (
        <React.Fragment>
          {textTitle}
        </React.Fragment>
      );
    } else if (!isBentobox)
      message = (
        <React.Fragment>
          Fix the{' '}
          <RedHighlight>
            <span id="failureCount">0</span> Issues
          </RedHighlight>{' '}
          Impacting<br />
          {domain}
        </React.Fragment>
      );
    else message = `We love seeing BentoBox websites!`;
    return message;
  }

  getSubtitle(message = null) {
    const { usFoods, failed, domain, name, isBentobox, unlocked } = this.props;
    if (usFoods && unlocked) {
      const calendlyLink = 'https://calendly.com/us-foods/websitegrader';

      message = (
        <React.Fragment>
          <Button
            onClick={() => window.open(calendlyLink, '_blank')}
            width="auto"
            padding="11px 2rem"
          >
            Book a consultation
          </Button>
        </React.Fragment>
      );
    }
    if (failed)
      message = `${domain} was omitted from our tool or something went wrong. This tool is ideal for individual restaurant websites. Try
                another website.`;
    else if (isBentobox) message = `${domain} is already on BentoBox.`;
    return message;
  }

  render() {
    const { unlocked, layout, failed, isBentobox, isStaff, usFoods } = this.props;
    const { shouldFixSidebar } = this.state;
    const shiftUp = -45;
    return (
      <React.Fragment>
        <WithTitle
          mt={70}
          smallTitleOnMobile
          title={this.getTitle()}
          subtitle={this.getSubtitle()}
          textAlign={'center'}
          shiftUp={shiftUp}
          id={this.id}
        >
          {failed ? (
            <Box
              width={[1 / 1, '400px']}
              mx="auto"
              px={['20px', 0]}
              style={{ textAlign: 'center' }}
            >
              <ResetButton />
            </Box>
          ) : (isStaff && !isBentobox) || unlocked ? (
            <ContentContainer flexDirection={isMobile ? 'column' : 'row'}>
              <ResultsActionsColumn
                upperBound={this.upperBound}
                shouldFixSidebar={shouldFixSidebar}
              />
              <Box
                width={[1 / 1, 1 / 1, 3 / 4]}
                ml={[0, 0, isMobile ? 0 : shouldFixSidebar ? '25%' : 0]}
              >
                <Report shiftUp={shiftUp} />
              </Box>
            </ContentContainer>
          ) : (
            <ContentContainer flexDirection="column">
              <Box style={{ maxWidth: '1150px' }} width={1 / 1} mx="auto">
                <CriteriaPreview shiftUp={shiftUp} />
                <UnlockForm layout={layout} shiftUp={shiftUp} />
              </Box>
            </ContentContainer>
          )}
        </WithTitle>
        <BackgroundImages initial={false} />
      </React.Fragment>
    );
  }
}

const mapStateToProps = ({
  usFoods,
  user: { unlocked, name },
  grade: {
    domain,
    is_bentobox: isBentobox,
    fail_count: failCount,
    failed,
    failure_reason,
    staff_request: isStaff,
  },
}) => {
  // Clean the domain (remove any excess chracters)
  const lastChar = domain[domain.length - 1];
  if (lastChar === '/') {
    domain = domain.slice(0, domain.length - 1);
  }

  return {
    usFoods,
    unlocked,
    failCount,
    failed,
    name,
    domain,
    isBentobox,
    isStaff,
  };
};

export default connect(mapStateToProps, null)(SearchResults);
